import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { BadgeCheck, BadgeX, CalendarRange, Hash, ScanLine, School, ShieldCheck } from "lucide-react";
import { LoadingState, Notice } from "@/components/common/States";
import FotoAnggota from "@/components/attendance/FotoAnggota";
import { Wordmark } from "@/components/common/Wordmark";
import { errorMessage, fetcher } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/labels";

const ROLE_CARD = {
  siswa: "Siswa", guru: "Guru", wali_kelas: "Guru & Wali Kelas", operator: "Pegawai ASN",
  pegawai_non_asn: "Pegawai Non-ASN", kepala_sekolah: "Kepala Sekolah", petugas_presensi: "Petugas Presensi",
  school_admin: "Pengurus Sekolah", orang_tua: "Orang Tua/Wali",
};

const tanggalID = (iso) => (iso
  ? new Intl.DateTimeFormat("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "long", year: "numeric" }).format(new Date(iso))
  : "—");

const Baris = ({ ikon: Ikon, label, nilai }) => nilai && (
  <div className="flex items-start gap-2.5 py-2.5">
    <Ikon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold leading-5 break-words">{nilai}</p>
    </div>
  </div>
);

/** Halaman yang dibuka lewat kode QR pada kartu: membuktikan kartu asli tanpa perlu masuk. */
export default function KartuPublik() {
  const { kunci } = useParams();
  const q = useQuery({ queryKey: ["kartu-publik", kunci], queryFn: () => fetcher(`/public/kartu/${kunci}`), retry: false });
  const data = q.data;
  const sah = data?.status_kartu === "active" && data?.status_anggota === "active";

  return (
    <div className="min-h-screen bg-slate-100 pb-10">
      <header className="border-b bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3.5">
          <Wordmark />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900/5 px-3 py-1 text-xs font-semibold text-slate-600">
            <ScanLine className="h-3.5 w-3.5" aria-hidden /> Verifikasi kartu
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-md px-4 pt-6">
        {q.isLoading ? <LoadingState rows={4} /> : q.error ? (
          <Notice tone={q.error.response?.status === 404 ? "warn" : "danger"} testId="kartu-publik-galat">
            {q.error.response?.status === 404
              ? "Kartu tidak dikenali. Pastikan kode QR dipindai dari kartu SIST, atau minta petugas memeriksa nomor serinya."
              : errorMessage(q.error)}
          </Notice>
        ) : (
          <div className="overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-black/5" data-testid="kartu-publik">
            <div className="h-1.5 w-full bg-gradient-to-r from-[#1E56C8] via-[#0B3D91] to-[#06224F]" aria-hidden />
            <div className="px-5 pt-5 text-center">
              <div className="mx-auto h-[74px] w-[59px] overflow-hidden rounded-xl shadow-md ring-2 ring-white">
                <FotoAnggota nama={data.nama} peran={data.peran} photo={data.photo}
                  className="h-full w-full object-cover" testId="kartu-publik-foto" />
              </div>
              <h1 className="mt-3 text-lg font-extrabold leading-tight tracking-tight">{data.nama || "Anggota sekolah"}</h1>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {ROLE_CARD[data.peran] || ROLE_LABELS[data.peran] || data.peran}
                {data.kelas ? ` · Kelas ${data.kelas}` : ""}
              </p>
              <p className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${
                sah ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-amber-50 text-amber-800 ring-1 ring-amber-200"
              }`} data-testid="kartu-publik-status">
                {sah ? <BadgeCheck className="h-4 w-4" aria-hidden /> : <BadgeSlash className="h-4 w-4" aria-hidden />}
                {sah ? "Kartu aktif & terdaftar" : data.status_kartu !== "active" ? "Kartu tidak aktif" : "Keanggotaan nonaktif"}
              </p>
            </div>

            <div className="mx-5 mt-4 rounded-2xl bg-slate-50 px-4">
              <Baris ikon={School} label="Sekolah" nilai={[data.sekolah?.name, data.sekolah?.city].filter(Boolean).join(" — ")} />
              <div className="h-px bg-slate-200" />
              <Baris ikon={Hash} label="Nomor seri kartu" nilai={data.seri} />
              <div className="h-px bg-slate-200" />
              <Baris ikon={CalendarRange} label="Berlaku" nilai={
                `T.A. ${data.tahun_ajaran || "—"}${data.berlaku_hingga ? ` · sampai ${tanggalID(data.berlaku_hingga)}` : ""}`
              } />
            </div>

            <div className="m-5 flex items-start gap-2.5 rounded-2xl border border-dashed bg-white px-4 py-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#0B3D91]" aria-hidden />
              <p className="text-[11px] leading-[17px] text-muted-foreground">
                Halaman ini hanya membuktikan kartu benar-benar diterbitkan sekolah. Nomor induk, kontak, dan alamat rumah
                sengaja tidak ditampilkan untuk umum.
              </p>
            </div>
          </div>
        )}
        <p className="mt-5 text-center text-[11px] text-muted-foreground">
          Meragukan kartu ini? Sampaikan ke petugas presensi di sekolah terkait.
        </p>
      </main>
    </div>
  );
}
